import { createSelector } from "@reduxjs/toolkit"
import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// Comment-library (F1, SPEC-033 / PRD-008). A team-scoped catalogue of the most-used standard
// comments per SIGNATURE CONTEXT (reviewer / supervisor / qa-release / return-for-correction /
// oos-conclusion). At a signature step, the ESignatureModal shows a "Standard comment" dropdown
// (only when given a `commentContext`); picking one merely PRE-FILLS the existing free-text
// reason — there is NO new structured field and NO change to how signatures/audit are stored, so
// the library is fully additive and back-compatible.
//
// The catalogue is editable team DATA (the texts are seeded in the BACKEND migration, NOT i18n
// keys) so a customer can curate it without a code release. Create/edit/enable are plain admin
// maintenance — NOT e-signed, only audit-tracked (the signature that USES a comment is still
// e-signed). Only a capability-admin (planner / qa-manager) may write; any LIMS user may read.
// Persisted via /lims/comment-templates (GET open; POST + PATCH gated server-side). See
// lims-api-contract.md.

// The signature contexts the library serves, known at build time. Extensible: adding a context
// is a data + caller edit, not a code rewrite. Mirrors the backend COMMENT_CONTEXTS.
export const COMMENT_CONTEXTS = [
  "reviewer",
  "supervisor",
  "qa-release",
  "return-for-correction",
  "oos-conclusion",
]

// i18n key for a context label (admin screen + dropdown group headings).
export function commentContextLabelKey(context) {
  if (context === "reviewer") return "limsCommentContextReviewer"
  if (context === "supervisor") return "limsCommentContextSupervisor"
  if (context === "qa-release") return "limsCommentContextQaRelease"
  if (context === "return-for-correction") return "limsCommentContextReturnForCorrection"
  if (context === "oos-conclusion") return "limsCommentContextOosConclusion"
  return ""
}

const initialState = { items: [], isLoading: false }

// Shared action type for both update() and setEnabled() (enable/disable is just a one-field
// edit, so it folds into the same reducer case — one place to maintain the replace logic).
const UPDATE_ACTION = "limsCommentLibrary/update"

// Stable dropdown order: by context, then sortOrder, then text (mirrors the server ordering).
function sortTemplates(items) {
  return [...items].sort((a, b) => {
    const byContext = (a.context || "").localeCompare(b.context || "")
    if (byContext !== 0) return byContext
    const byOrder = (a.sortOrder || 0) - (b.sortOrder || 0)
    if (byOrder !== 0) return byOrder
    return (a.text || "").localeCompare(b.text || "")
  })
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsCommentLibrary/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsCommentLibrary/fetch":
      return { items: sortTemplates(action.res), isLoading: false }
    case "limsCommentLibrary/create":
      return { ...state, items: sortTemplates([...state.items, action.res]) }
    case UPDATE_ACTION:
      return {
        ...state,
        items: sortTemplates(state.items.map((x) => (x.id === action.res.id ? action.res : x))),
      }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsCommentLibrary/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/comment-templates").then((res) => res.items)
})

export const create = asyncAction("limsCommentLibrary/create", (_dispatch, template) => {
  return limsRequest("POST", "/lims/comment-templates", { input: template }).then((res) => res.item)
})

export const update = asyncAction(UPDATE_ACTION, (_dispatch, id, patch) => {
  return limsRequest("PATCH", `/lims/comment-templates/${id}`, { patch }).then((res) => res.item)
})

// Enable/disable a template (retire without deleting, so historical audit text stays readable).
// A thin alias of update — the server treats it as a normal edit (one audit action).
export const setEnabled = asyncAction(UPDATE_ACTION, (_dispatch, id, enabled) => {
  return limsRequest("PATCH", `/lims/comment-templates/${id}`, { patch: { enabled } }).then((res) => res.item)
})

export const selectCommentTemplates = (state) => state.limsCommentLibrary.items

// ENABLED templates for a given signature context (what the signer dropdown offers). Memoised
// per context so the dropdown doesn't re-render on unrelated state changes. A disabled template
// is hidden from the signer but still exists for the admin screen + audit readability.
const selectByContextMemo = createSelector(
  [(state) => state.limsCommentLibrary.items, (_state, context) => context],
  (items, context) => items.filter((t) => t.context === context && t.enabled !== false)
)
export const selectByContext = (state, context) => selectByContextMemo(state, context)
